import { FC } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom";
import { DateTime } from "luxon";
import { CheckCircle } from "@mui/icons-material";
import { grey } from "@mui/material/colors";
import { makeSxStyles } from "ui";

export interface EndShiftDialogProps
  extends Omit<
    DialogProps,
    "onClose" | "open" | "style" | "inputValue" | "handler"
  > {
  onConfirm: () => void;
  onClose: () => void;
  open?: boolean;
  remainingOrdersNr: number;
  status?: "success" | "loading";
  onSecondModalConfirm: () => void;
}

export const EndShiftDialog: FC<EndShiftDialogProps> = (props) => {
  const {
    sx,
    onConfirm,
    onClose,
    open,
    remainingOrdersNr,
    status,
    onSecondModalConfirm,
    ...rest
  } = props;

  const styles = useStyles();
  const { date } = useParams<{ date: string }>();
  if (!date) return;

  const parsedDate = DateTime.fromFormat(date, "MM-dd-yyyy");
  const formattedDate = parsedDate.toFormat("d MMMM yyyy");

  return (
    <Dialog onClose={onClose} {...rest} sx={sx || {}} open={open || false}>
      {status === "loading" ? (
        <DialogContent sx={styles.dialogContent}>
          <CircularProgress />
          <Box
            sx={{
              marginTop: "40px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "16px",
              color: grey[100],
              textAlign: "center",
            }}
          >
            <Typography variant="bodyMedium">Ending shift...</Typography>
          </Box>
        </DialogContent>
      ) : status === "success" ? (
        <DialogContent sx={styles.dialogContent}>
          <CheckCircle
            sx={{ width: "60px", height: "60px", color: "#8ACEFF" }}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
              gap: "16px",
              color: grey[100],
              textAlign: "center",
            }}
          >
            <Typography variant="titleLarge">Shift Ended</Typography>
            <Typography variant="bodyMedium">
              {remainingOrdersNr} addresses were not updated when the shift
              ended.
            </Typography>
          </Box>
          <Button variant="contained" onClick={onSecondModalConfirm}>
            Got it
          </Button>
        </DialogContent>
      ) : (
        <>
          <DialogTitle>End Shift {formattedDate}</DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ marginY: "16px" }}>
              Are you sure you want to end this shift?
              <br />
              {remainingOrdersNr} addresses haven’t been updated. You will still
              be able to update their statuses manually.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={onClose}
              sx={{ color: "#8ACEFF" }}
              data-testid="cancel-button"
            >
              Cancel
            </Button>
            <Button
              onClick={onConfirm}
              variant="contained"
              sx={{
                backgroundColor: "#8ACEFF",
                color: "#00344E",
              }}
              data-testid="confirm-button"
            >
              End Shift
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

const useStyles = makeSxStyles(() => ({
  dialogContent: {
    padding: "56px",
    display: "flex",
    flexDirection: "column",
    gap: "40px",
    alignItems: "center",
  },
}));
